import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { invalidateBalanceQueries } from "../utils/query-invalidation";

// Define the structure for override entries
interface OverrideEntry<T = unknown> {
  enabled: boolean;
  value: T;
}

// Define available override types for type safety
interface OverrideRegistry {
  balance: string;
  // Add more override types here in the future
  // e.g., gasPrice: number;
  // transactionSpeed: 'slow' | 'medium' | 'fast';
}

// Generic type for override keys
type OverrideKey = keyof OverrideRegistry;

// Storage key for the overrides object
const OVERRIDES_STORAGE_KEY = "dev-overrides";

// Type for the complete overrides object
type OverridesState = {
  [K in OverrideKey]?: OverrideEntry<OverrideRegistry[K]>;
};

/**
 * Generic hook for managing development overrides
 * Stores all overrides in a single localStorage object
 */
export function useOverrides() {
  const [overrides, setOverrides] = useState<OverridesState>({});
  const queryClient = useQueryClient();

  const invalidateQueries = useCallback(() => {
    invalidateBalanceQueries(queryClient);
  }, [queryClient]);

  // Load overrides from localStorage and listen for changes
  useEffect(() => {
    const loadOverrides = () => {
      try {
        const stored = localStorage.getItem(OVERRIDES_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as OverridesState;
          setOverrides(parsed);
        } else {
          setOverrides({});
        }
      } catch {
        // Silently fail if localStorage is not available
        setOverrides({});
      }
    };

    // Load initial overrides
    loadOverrides();

    // Listen for localStorage changes (including from other tabs/components)
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === OVERRIDES_STORAGE_KEY) {
        loadOverrides();
      }
    };

    // Listen for custom events for same-tab updates
    const handleCustomStorageChange = () => {
      loadOverrides();
      invalidateQueries();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("overrides-changed", handleCustomStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("overrides-changed", handleCustomStorageChange);
    };
  }, [invalidateQueries]);

  // Save overrides to localStorage whenever they change
  const saveOverrides = (newOverrides: OverridesState) => {
    try {
      localStorage.setItem(OVERRIDES_STORAGE_KEY, JSON.stringify(newOverrides));
      setOverrides(newOverrides);
      invalidateQueries();

      // Dispatch custom event to notify other components in the same tab
      window.dispatchEvent(new CustomEvent("overrides-changed"));
    } catch {
      // Silently fail if localStorage is not available
    }
  };

  // Set an override value
  const setOverride = <K extends OverrideKey>(
    key: K,
    enabled: boolean,
    value?: OverrideRegistry[K],
  ) => {
    const newOverrides = { ...overrides };

    if (enabled && value !== undefined) {
      newOverrides[key] = { enabled: true, value };
    } else if (!enabled && overrides[key]?.value !== undefined) {
      // Keep the existing value when disabling
      newOverrides[key] = { enabled: false, value: overrides[key]?.value };
    } else if (!enabled && value !== undefined) {
      // Use provided value when disabling
      newOverrides[key] = { enabled: false, value };
    }

    saveOverrides(newOverrides);
  };

  // Get an override value
  const getOverride = <K extends OverrideKey>(
    key: K,
  ): OverrideEntry<OverrideRegistry[K]> | undefined => {
    return overrides[key];
  };

  // Check if an override is enabled and get its value
  const getEnabledOverride = <K extends OverrideKey>(key: K): OverrideRegistry[K] | undefined => {
    const override = overrides[key];
    return override?.enabled ? override.value : undefined;
  };

  // Clear a specific override
  const clearOverride = (key: OverrideKey) => {
    const newOverrides = { ...overrides };
    delete newOverrides[key];
    saveOverrides(newOverrides);
  };

  // Clear all overrides
  const clearAllOverrides = () => {
    try {
      localStorage.removeItem(OVERRIDES_STORAGE_KEY);
      setOverrides({});
      invalidateQueries();

      // Dispatch custom event to notify other components in the same tab
      window.dispatchEvent(new CustomEvent("overrides-changed"));
    } catch {
      // Silently fail if localStorage is not available
    }
  };

  // Get all override keys that are currently enabled
  const getEnabledOverrideKeys = (): OverrideKey[] => {
    return (Object.keys(overrides) as OverrideKey[]).filter(
      (key) => overrides[key]?.enabled === true,
    );
  };

  return {
    overrides,
    setOverride,
    getOverride,
    getEnabledOverride,
    clearOverride,
    clearAllOverrides,
    getEnabledOverrideKeys,
  };
}
